import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import { Base64 } from 'js-base64';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import './desktop-password-reset-process-style.scss';
import { useSnackbar } from '@src/providers/SnackbarProvider';

import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import VisibilityIcon from '@src/componentsNew/Icons/Icons/VisibilityIcon';
import VisibilityOffIcon from '@src/componentsNew/Icons/Icons/VisibilityOffIcon';

import { SET_PASSWORD } from './queries';
import { setPassword } from './__generated__/setPassword';

import { accountLoginUrl } from '@src/app/routes';
import { getGraphqlIdFromDBId } from '@utils';
import { padBase64 } from '@src/utils/deepLink';

interface RouteParams {
  uidb64: string;
  token: string;
}

const DesktopPasswordResetProcessPage = () => {
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const { uidb64, token } = useParams<RouteParams>();

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');

  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordConfirmError, setPasswordConfirmError] = useState('');

  const [setPasswordMutation, { loading }] = useMutation<setPassword>(SET_PASSWORD, {
    onCompleted: (data) => {
      const errors = data?.setPassword?.errors;

      if (0 < errors.length) {
        showSnackbar({ message: errors[0].message });
      } else {
        showSnackbar({ message: 'Password Successfully Changed' });

        setTimeout(() => {
          history.push(accountLoginUrl);
        }, 2000);
      }
    },
  });

  const validatePassword = () => {
    if (newPassword.trim().length === 0 && passwordConfirm.trim().length === 0) {
      setNewPasswordError('Please enter this field');
      setPasswordConfirmError('Please enter this field');
      return false;
    }

    if (newPassword.trim().length === 0) {
      setNewPasswordError('Please enter this field');
      setPasswordConfirmError('');

      return false;
    }

    if (passwordConfirm.trim().length === 0) {
      setNewPasswordError('');
      setPasswordConfirmError('Please enter this field');

      return false;
    }

    if (newPassword !== passwordConfirm) {
      setNewPasswordError('');
      setPasswordConfirmError('The password confirmation must match the provided passwords');

      return false;
    }

    return true;
  };

  const handleClick = (e: any) => {
    if (!validatePassword()) {
      return;
    }

    setNewPasswordError('');
    setPasswordConfirmError('');

    const userDbId = atob(padBase64(uidb64));
    const id = getGraphqlIdFromDBId(userDbId, 'User');

    setPasswordMutation({
      variables: {
        id,
        input: {
          password: newPassword,
          token,
        },
      },
    });
  };

  return (
    <div id="desktop-password-reset-process-wrapper">
      <div className="password-reset-process-content-wrapper">
        <Typography variant="h6" component="h4">
          Reset Password
        </Typography>

        <Typography cName="info-text" variant="body1" component="p">
          {'Now you can set new password\nfor your account.'}
        </Typography>

        <div className="password-input-wrapper">
          <TextField
            type={showNewPassword ? 'text' : 'password'}
            size="medium"
            className="password-input"
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={!!newPasswordError}
            helperText={newPasswordError}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword((prev) => !prev)} edge="end">
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            type={showPasswordConfirm ? 'text' : 'password'}
            size="medium"
            className="password-input"
            label="Password Confirmation"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            error={!!passwordConfirmError}
            helperText={passwordConfirmError}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPasswordConfirm((prev) => !prev)} edge="end">
                    {showPasswordConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <Button size="large" cName="submit-btn" variant="contained" disabled={loading} onClick={handleClick}>
          Reset Password
        </Button>
      </div>
    </div>
  );
};

export default DesktopPasswordResetProcessPage;
